import { Box, Button, Skeleton, Typography } from '@mui/material';
import { Globe, Plus } from 'lucide-react';
import { useState } from 'react';
import { IconTile } from 'src/components/IconTile';
import { type SelectMenuOption } from 'src/components/SelectMenu';
import {
  SETTINGS_SECTION_ROW_PADDING,
  SettingsSectionCard,
} from 'src/components/SettingsSection';
import { useBrands } from 'src/graphql/entities/brand';
import { type CgCustomDomain } from 'src/graphql/entities/cg-custom-domain/cg-custom-domain.types';
import { useCgCustomDomains } from 'src/graphql/entities/cg-custom-domain/use-cg-custom-domain';
import { useManageCgCustomDomain } from 'src/graphql/entities/cg-custom-domain/use-manage-cg-custom-domain';
import { useClientGatewayUrl } from 'src/graphql/entities/client-gateway-url/use-client-gateway-url';
import { usePartnerDomains } from 'src/graphql/entities/partner-domain';
import { useGlobalStore } from 'src/store';
import { Modals, useShowModals } from 'src/store/modals';
import { useConfirmationModal } from 'src/utils/hooks/use-confirmation-modal';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { CgCustomDomainCard } from './cg-custom-domain-card';
import {
  CgCustomDomainDisplayState,
  FALLBACK_EXAMPLE_APEX,
  getCgCustomDomainDisplayState,
} from './cg-custom-domain-display';
import { getGraphQLErrorMessage } from './get-graphql-error-message';
import { mutedSx, panelSx } from './styles';

const UNTITLED_SEGMENT = 'Untitled segment';

export const CgCustomDomainPanel = () => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { brands, loading: segmentsLoading } = useBrands(partnerId);
  const { domains, loading: domainsLoading } = useCgCustomDomains(partnerId);
  const { checkNow, removeDomain, setSegments } = useManageCgCustomDomain(partnerId);
  const currentAddress = useClientGatewayUrl(partnerId).url?.replace(/^https?:\/\//, '');
  const { partnerDomains } = usePartnerDomains(partnerId);

  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [showAddModal] = useShowModals(Modals.AddCgCustomDomain);
  const { confirm } = useConfirmationModal();
  const { showSuccessAlert, showInfoAlert, showErrorAlert } = useNotifications();

  const segments = brands.map(({ brandId, name }) => ({
    brandId,
    name: name || UNTITLED_SEGMENT,
  }));
  const segmentName = (brandId: string) =>
    segments.find((segment) => segment.brandId === brandId)?.name ?? UNTITLED_SEGMENT;

  // A segment is on one domain at most, so picking it for this one moves it here.
  const segmentOptionsFor = (domain: CgCustomDomain): SelectMenuOption[] =>
    segments.map(({ brandId, name }) => {
      const elsewhere = domains.find(
        (other) =>
          other.partnerCustomDomainId !== domain.partnerCustomDomainId &&
          other.brandIds.includes(brandId),
      );

      return {
        value: brandId,
        title: name,
        description: elsewhere ? `On ${elsewhere.hostname}` : undefined,
      };
    });

  const openAddModal = () => showAddModal();

  const handleSegmentsChange = async (domain: CgCustomDomain, brandIds: string[]) => {
    try {
      await setSegments(domain, brandIds);
    } catch (error) {
      showErrorAlert(
        getGraphQLErrorMessage(error, 'Couldn’t update the client segments. Try again.'),
      );
    }
  };

  const handleCheckNow = async (domain: CgCustomDomain) => {
    const before = getCgCustomDomainDisplayState(domain);

    setCheckingId(domain.partnerCustomDomainId);

    try {
      const checked = await checkNow(domain.partnerCustomDomainId);

      if (!checked) return;

      const after = getCgCustomDomainDisplayState(checked);

      if (after === CgCustomDomainDisplayState.Live) {
        showSuccessAlert(`${checked.hostname} is live`);
      } else {
        showInfoAlert(
          after === before
            ? 'Checked just now. Nothing has changed yet.'
            : 'Checked just now. Status updated.',
        );
      }
    } catch (error) {
      showErrorAlert(getGraphQLErrorMessage(error, 'Couldn’t check the domain. Try again.'));
    } finally {
      setCheckingId(null);
    }
  };

  const handleRemove = async (domain: CgCustomDomain) => {
    const confirmed = await confirm({
      title: `Remove ${domain.hostname}?`,
      body: domain.brandIds.length
        ? `${domain.brandIds.map(segmentName).join(', ')} go back to their ProVision address right away. You can delete the DNS record afterward.`
        : 'You can delete the DNS record afterward.',
      confirmLabel: 'Remove domain',
      danger: true,
    });

    if (!confirmed) return;

    try {
      await removeDomain(domain.partnerCustomDomainId);
      showSuccessAlert(`${domain.hostname} removed`);
    } catch (error) {
      showErrorAlert(getGraphQLErrorMessage(error, 'Couldn’t remove the domain. Try again.'));
    }
  };

  const isLoading = (segmentsLoading && !brands.length) || (domainsLoading && !domains.length);

  return (
    <Box sx={panelSx}>
      <Typography variant="body2" sx={mutedSx}>
        Serve a client segment’s membership pages and client sign-in on your own web address.
        Add the domain, add the DNS record it shows, and we take it from there.
      </Typography>

      {isLoading ? <Skeleton variant="rounded" height={160} /> : null}

      {!isLoading &&
        domains.map((domain) => (
          <CgCustomDomainCard
            key={domain.partnerCustomDomainId}
            domain={domain}
            segmentOptions={segmentOptionsFor(domain)}
            onSegmentsChange={(brandIds) => handleSegmentsChange(domain, brandIds)}
            isChecking={checkingId === domain.partnerCustomDomainId}
            onCheckNow={() => handleCheckNow(domain)}
            onRemove={() => handleRemove(domain)}
          />
        ))}

      {!isLoading && !domains.length ? (
        <SettingsSectionCard>
          <Box
            sx={{
              ...SETTINGS_SECTION_ROW_PADDING,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
            }}
          >
            <IconTile>
              <Globe />
            </IconTile>

            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                No custom domain yet
              </Typography>
              <Typography variant="body2" sx={{ ...mutedSx, mt: 0.5 }}>
                {currentAddress
                  ? `Right now your membership pages and client sign-in are at ${currentAddress}. `
                  : ''}
                With a custom domain they can be at your own address instead, like membership.
                {partnerDomains[0]?.domain ?? FALLBACK_EXAMPLE_APEX}.
              </Typography>
              <Typography variant="body2" sx={{ ...mutedSx, mt: 1 }}>
                Add one and we show the DNS record to add where you manage DNS. We check it and
                tell you when it’s live, then you choose the client segments it serves.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<Plus size={16} />}
                onClick={openAddModal}
                sx={{ mt: 2 }}
              >
                Add domain
              </Button>
            </Box>
          </Box>
        </SettingsSectionCard>
      ) : null}

      {!isLoading && domains.length ? (
        <Box>
          <Button size="small" startIcon={<Plus size={16} />} onClick={openAddModal}>
            Add domain
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};
